import {
  createScalarDraftAdapter,
  defineNavigationQuerySchema,
  navigationQuery,
  type NavigationQuerySchema,
  type SessionStateAdapter,
} from '../../navigation';
import {
  SALES_TAB_KEYS,
  resolveAppBasename,
  toDocumentPath,
  toRouterPath,
} from '../../navigation/routePolicy';

export { SALES_TAB_KEYS } from '../../navigation/routePolicy';

export type SalesTabKey = (typeof SALES_TAB_KEYS)[number];

export interface SalesLocationLike {
  pathname: string;
  search: string;
  hash: string;
}

export interface CanonicalSalesLocation extends SalesLocationLike {
  tab: SalesTabKey;
}

export type SalesManualLeadDraft = {
  full_name: string;
  name_ar: string;
  name_en: string;
  name_ar_manual: string;
  name_en_manual: string;
  phone: string;
  email: string;
  location_hint: string;
  course: string;
  source: string;
  note: string;
  branch: string;
  source_type: string;
  source_details: string;
  referred_by_lead_id: string;
  referred_by_student_id: string;
  other_referrer: string;
  proof_image_url: string;
  referrer_label: string;
  returned_request_id: string;
}

export const salesManualLeadDraftAdapter: SessionStateAdapter<SalesManualLeadDraft> =
  createScalarDraftAdapter<SalesManualLeadDraft>({
    full_name: '',
    name_ar: '',
    name_en: '',
    name_ar_manual: '',
    name_en_manual: '',
    phone: '',
    email: '',
    location_hint: '',
    course: '',
    source: '',
    note: '',
    branch: '',
    source_type: '',
    source_details: '',
    referred_by_lead_id: '',
    referred_by_student_id: '',
    other_referrer: '',
    proof_image_url: '',
    referrer_label: '',
    returned_request_id: '',
  });

export const salesPipelineViewStateAdapter = createScalarDraftAdapter<{
  focusBoardCollapsed: boolean;
  completionQueueCollapsed: boolean;
}>({
  focusBoardCollapsed: false,
  completionQueueCollapsed: false,
});

export type SalesTaskCompleteDraft = {
  outcome: string;
  notes: string;
};

export type SalesTaskScheduleDraft = {
  date: string;
  time: string;
  note: string;
};

export type SalesTaskQuickActionDraft = {
  outcome: string;
  notes: string;
  stageTag: string;
  followUpAt: string;
  template: string;
};

export type SalesHistoryNoteDraft = {
  note: string;
  edited: boolean;
};

export const salesTaskCompleteDraftAdapter = createScalarDraftAdapter<SalesTaskCompleteDraft>({
  outcome: '',
  notes: '',
});

export const salesTaskSnoozeDraftAdapter = createScalarDraftAdapter<SalesTaskScheduleDraft>({
  date: '',
  time: '',
  note: '',
});

export const salesTaskRescheduleDraftAdapter = createScalarDraftAdapter<SalesTaskScheduleDraft>({
  date: '',
  time: '',
  note: '',
});

export const salesTaskQuickActionDraftAdapter = createScalarDraftAdapter<SalesTaskQuickActionDraft>({
  outcome: '',
  notes: '',
  stageTag: '',
  followUpAt: '',
  template: '',
});

const persistedSalesHistoryNoteDraftAdapter = createScalarDraftAdapter<{ note: string }>({
  note: '',
});

export const salesHistoryNoteDraftAdapter: SessionStateAdapter<SalesHistoryNoteDraft> = Object.freeze({
  initial: () => ({ note: '', edited: false }),
  encode: (value: SalesHistoryNoteDraft) => persistedSalesHistoryNoteDraftAdapter.encode({ note: value.note }),
  decode: (value: unknown) => {
    const persisted = persistedSalesHistoryNoteDraftAdapter.decode(value);
    return persisted ? { note: persisted.note, edited: true } : null;
  },
  isDirty: (value: SalesHistoryNoteDraft) => value.edited,
});

const SALES_TAB_KEY_SET = new Set<string>(SALES_TAB_KEYS);

export const SALES_PIPELINE_QUERY_KEYS = [
  'q',
  'month',
  'from',
  'to',
  'courses',
  'campaigns',
  'stages',
  'tags',
  'fields',
  'employee',
  'employees',
  'sort',
  'mode',
  'work',
  'queue',
  'density',
  'focus',
  'panel',
  'lead',
  'leadTab',
  'lead_id',
  'leadId',
] as const;

const LEAD_QUERY_KEYS = new Set(['lead', 'lead_id', 'leadId']);
const STRICT_POSITIVE_ID = /^(?:[1-9]\d*)$/;
const PIPELINE_MONTH = /^(\d{4})-(\d{2})$/;
const PIPELINE_FIELD_SET = new Set([
  'tags',
  'source',
  'lastActivity',
  'nextAction',
  'dueDate',
]);

export const salesPipelineSchema = defineNavigationQuerySchema({
  q: navigationQuery.string('q', { defaultValue: '', maxLength: 120 }),
  month: navigationQuery.string('month', { defaultValue: '', maxLength: 7 }),
  from: navigationQuery.date('from'),
  to: navigationQuery.date('to'),
  courses: navigationQuery.stringList('courses'),
  campaigns: navigationQuery.stringList('campaigns'),
  stages: navigationQuery.stringList('stages'),
  tags: navigationQuery.stringList('tags'),
  fields: navigationQuery.stringList('fields'),
  employee: navigationQuery.id('employee'),
  employees: navigationQuery.stringList('employees', { maxItems: 200, maxItemLength: 20 }),
  sort: navigationQuery.enum(
    'sort',
    ['newest', 'oldest', 'followup_due', 'overdue_first', 'last_activity'] as const,
    'newest',
  ),
  mode: navigationQuery.enum(
    'mode',
    ['daily_work', 'active_funnel', 'imported_backlog', 'closed_system', 'all'] as const,
    'daily_work',
  ),
  work: navigationQuery.enum('work', ['', 'needs_attention', 'new_untouched', 'overdue', 'hot', 'no_answer'] as const, ''),
  queue: navigationQuery.enum('queue', ['', 'due_today', 'waiting', 'scheduled_later'] as const, ''),
  density: navigationQuery.enum('density', ['comfortable', 'compact'] as const, 'compact'),
  focus: navigationQuery.id('focus'),
  panel: navigationQuery.enum('panel', ['', 'filters', 'view', 'create-lead'] as const, ''),
  lead: navigationQuery.id('lead'),
  leadTab: navigationQuery.enum(
    'leadTab',
    ['overview', 'workflow', 'activity', 'conversion'] as const,
    'overview',
  ),
});

const LEAD_TAB_VALUES = ['overview', 'workflow', 'activity', 'conversion'] as const;
const TASK_AGING_VALUES = ['lt24', 'd1_7', 'gt7', 'gt30'] as const;
const TASK_SCORE_VALUES = ['hot', 'warm', 'cold', 'high_score'] as const;

export const salesTasksSchema = defineNavigationQuerySchema({
  taskTab: navigationQuery.enum(
    'taskTab',
    ['must_do_now', 'due_today', 'overdue', 'upcoming', 'completed'] as const,
    'must_do_now',
  ),
  q: navigationQuery.string('q', { defaultValue: '', maxLength: 120 }),
  courses: navigationQuery.stringList('courses', { maxItems: 50, maxItemLength: 120 }),
  stages: navigationQuery.stringList('stages', { maxItems: 50, maxItemLength: 120 }),
  types: navigationQuery.stringList('types', { maxItems: 50, maxItemLength: 120 }),
  priorities: navigationQuery.stringList('priorities', { maxItems: 50, maxItemLength: 120 }),
  sources: navigationQuery.stringList('sources', { maxItems: 50, maxItemLength: 120 }),
  campaigns: navigationQuery.stringList('campaigns', { maxItems: 50, maxItemLength: 120 }),
  due: navigationQuery.string('due', { defaultValue: '', maxLength: 50 }),
  aging: navigationQuery.stringList('aging', {
    allowedValues: TASK_AGING_VALUES,
    maxItems: TASK_AGING_VALUES.length,
  }),
  score: navigationQuery.stringList('score', {
    allowedValues: TASK_SCORE_VALUES,
    maxItems: TASK_SCORE_VALUES.length,
  }),
  next: navigationQuery.enum('next', ['all', 'yes', 'no'] as const, 'all'),
  noActivity: navigationQuery.boolean('noActivity'),
  quick: navigationQuery.enum(
    'quick',
    ['none', 'hot_leads', 'payment_followup', 'no_answer_retry', 'due_now', 'overdue_today', 'stale_tasks'] as const,
    'none',
  ),
  sort: navigationQuery.enum(
    'sort',
    ['due_time', 'priority', 'lead_score', 'aging', 'stage', 'last_activity'] as const,
    'due_time',
  ),
  order: navigationQuery.enum('order', ['asc', 'desc'] as const, 'asc'),
  limit: navigationQuery.integer('limit', { defaultValue: 40, min: 40, max: 600, step: 40 }),
  lead: navigationQuery.id('lead'),
  leadTab: navigationQuery.enum('leadTab', LEAD_TAB_VALUES, 'overview'),
  taskAction: navigationQuery.enum(
    'taskAction',
    ['', 'complete', 'snooze', 'reschedule', 'call', 'whatsapp', 'note', 'followup', 'outcome'] as const,
    '',
  ),
  taskIds: navigationQuery.stringList('taskIds', { maxItems: 100, maxItemLength: 100 }),
  selected: navigationQuery.stringList('selected', { maxItems: 100, maxItemLength: 100 }),
});

export const salesTodaySchema = defineNavigationQuerySchema({
  focus: navigationQuery.string('focus', { defaultValue: '', maxLength: 120 }),
  lead: navigationQuery.id('lead'),
  leadTab: navigationQuery.enum('leadTab', LEAD_TAB_VALUES, 'overview'),
});

export const salesHistorySchema = defineNavigationQuerySchema({
  preset: navigationQuery.enum('preset', ['today', 'yesterday', 'week', 'custom'] as const, 'today'),
  view: navigationQuery.enum('view', ['all', 'contact', 'tasks', 'notes', 'workflow'] as const, 'all'),
  from: navigationQuery.date('from'),
  to: navigationQuery.date('to'),
  q: navigationQuery.string('q', { defaultValue: '', maxLength: 120 }),
  categories: navigationQuery.stringList('categories', { maxItems: 50, maxItemLength: 120 }),
  eventTypes: navigationQuery.stringList('eventTypes', { maxItems: 50, maxItemLength: 120 }),
  courses: navigationQuery.stringList('courses', { maxItems: 50, maxItemLength: 120 }),
  stages: navigationQuery.stringList('stages', { maxItems: 50, maxItemLength: 120 }),
  employee: navigationQuery.id('employee'),
  notes: navigationQuery.boolean('notes'),
  important: navigationQuery.boolean('important'),
  taskRelated: navigationQuery.boolean('taskRelated'),
  contactRelated: navigationQuery.boolean('contactRelated'),
  leadQ: navigationQuery.string('leadQ', { defaultValue: '', maxLength: 120 }),
  summary: navigationQuery.enum(
    'summary',
    ['', 'callsLogged', 'whatsappActions', 'emailsSent', 'notesAdded', 'followUpsCreated', 'tasksCompleted', 'stageChanges', 'leadsOpened'] as const,
    '',
  ),
  editNote: navigationQuery.id('editNote'),
});

export const salesPerformanceSchema = defineNavigationQuerySchema({
  range: navigationQuery.enum('range', ['today', 'month', 'all', 'custom'] as const, 'today'),
  from: navigationQuery.date('from'),
  to: navigationQuery.date('to'),
  employee: navigationQuery.id('employee'),
  status: navigationQuery.string('status', { defaultValue: '', maxLength: 80 }),
  leadStatus: navigationQuery.string('leadStatus', { defaultValue: '', maxLength: 80 }),
  employeeTab: navigationQuery.enum(
    'employeeTab',
    ['overview', 'revenue', 'sources', 'leads', 'activity'] as const,
    'overview',
  ),
  employeeCourse: navigationQuery.string('employeeCourse', { defaultValue: '', maxLength: 120 }),
  employeeSource: navigationQuery.string('employeeSource', { defaultValue: '', maxLength: 120 }),
  employeeTag: navigationQuery.string('employeeTag', { defaultValue: '', maxLength: 120 }),
  teamPage: navigationQuery.integer('teamPage', { defaultValue: 1, min: 1, max: 10_000 }),
  teamPageSize: navigationQuery.integer('teamPageSize', { defaultValue: 15, min: 1, max: 100 }),
  teamSort: navigationQuery.enum(
    'teamSort',
    ['', 'leads_assigned', 'leads_contacted', 'followups_completed', 'conversions_count', 'conversion_rate', 'avg_response_time_seconds', 'revenue_from_first_invoices'] as const,
    '',
  ),
  teamOrder: navigationQuery.enum('teamOrder', ['asc', 'desc'] as const, 'asc'),
  leadPage: navigationQuery.integer('leadPage', { defaultValue: 1, min: 1, max: 10_000 }),
  leadPageSize: navigationQuery.integer('leadPageSize', { defaultValue: 10, min: 1, max: 100 }),
  enrolledPage: navigationQuery.integer('enrolledPage', { defaultValue: 1, min: 1, max: 10_000 }),
  enrolledPageSize: navigationQuery.integer('enrolledPageSize', { defaultValue: 10, min: 1, max: 100 }),
});

export const salesManagerDashboardSchema = defineNavigationQuerySchema({
  range: navigationQuery.enum('range', ['month', 'today', 'all', 'custom'] as const, 'month'),
  date_from: navigationQuery.date('date_from'),
  date_to: navigationQuery.date('date_to'),
  branch: navigationQuery.string('branch', { defaultValue: '', maxLength: 100 }),
  team_id: navigationQuery.id('team_id'),
  salesman_id: navigationQuery.id('salesman_id'),
  course: navigationQuery.string('course', { defaultValue: '', maxLength: 100 }),
  source: navigationQuery.string('source', { defaultValue: '', maxLength: 80 }),
  payment_method: navigationQuery.string('payment_method', { defaultValue: '', maxLength: 50 }),
  ad_name: navigationQuery.string('ad_name', { defaultValue: '', maxLength: 120 }),
  adset_name: navigationQuery.string('adset_name', { defaultValue: '', maxLength: 120 }),
  campaign_name: navigationQuery.string('campaign_name', { defaultValue: '', maxLength: 120 }),
  campaign_id: navigationQuery.string('campaign_id', { defaultValue: '', maxLength: 32 }),
  panel: navigationQuery.enum('panel', ['', 'filters'] as const, ''),
});

function schemaQueryKeys<T extends Record<string, unknown>>(
  schema: NavigationQuerySchema<T>,
): readonly string[] {
  return schema.fieldNames.map((fieldName) => schema.fields[fieldName].key);
}

export const SALES_TAB_QUERY_KEYS: Readonly<Partial<Record<SalesTabKey, readonly string[]>>> =
  Object.freeze({
    pipeline: Object.freeze([...schemaQueryKeys(salesPipelineSchema), 'lead_id', 'leadId']),
    tasks: Object.freeze(schemaQueryKeys(salesTasksSchema)),
    today: Object.freeze(schemaQueryKeys(salesTodaySchema)),
    history: Object.freeze(schemaQueryKeys(salesHistorySchema)),
    performance: Object.freeze(schemaQueryKeys(salesPerformanceSchema)),
    manager_dashboard: Object.freeze(schemaQueryKeys(salesManagerDashboardSchema)),
    lead_movement: Object.freeze(['movement_tab']),
  });

const ALL_SALES_QUERY_KEY_SET = new Set<string>(
  Object.values(SALES_TAB_QUERY_KEYS).flatMap((keys) => keys ?? []),
);

function salesTabQueryKeySet(tab: SalesTabKey): ReadonlySet<string> {
  return new Set(SALES_TAB_QUERY_KEYS[tab] ?? []);
}

export type SalesPipelineNavigationValues = {
  q: string;
  month: string;
  from: string | null;
  to: string | null;
  courses: string[];
  campaigns: string[];
  stages: string[];
  tags: string[];
  fields: string[];
  employee: number | null;
  employees: string[];
  sort: 'newest' | 'oldest' | 'followup_due' | 'overdue_first' | 'last_activity';
  mode: 'daily_work' | 'active_funnel' | 'imported_backlog' | 'closed_system' | 'all';
  work: '' | 'needs_attention' | 'new_untouched' | 'overdue' | 'hot' | 'no_answer';
  queue: '' | 'due_today' | 'waiting' | 'scheduled_later';
  density: 'comfortable' | 'compact';
  focus: number | null;
  panel: '' | 'filters' | 'view' | 'create-lead';
  lead: number | null;
  leadTab: 'overview' | 'workflow' | 'activity' | 'conversion';
};

export function normalizeSalesPipelineMonth(value: string): string {
  if (value === '') return '';
  const match = PIPELINE_MONTH.exec(value);
  if (!match) return '';
  const month = Number(match[2]);
  return month >= 1 && month <= 12 ? value : '';
}

export function normalizeSalesPipelineFields(values: readonly string[]): string[] {
  return [...new Set(values.filter((value) => PIPELINE_FIELD_SET.has(value)))].sort();
}

export function isSalesTabKey(value: unknown): value is SalesTabKey {
  return typeof value === 'string' && SALES_TAB_KEY_SET.has(value);
}

export function canonicalSalesTabPath(tab: SalesTabKey): string {
  return `/sales/${tab}`;
}

function pathTab(pathname: string): string {
  if (pathname === '/sales' || pathname === '/sales/') return '';
  if (!pathname.startsWith('/sales/')) return '';
  return pathname.slice('/sales/'.length).split('/')[0] ?? '';
}

function positiveId(value: string | null): string | null {
  if (!value || !STRICT_POSITIVE_ID.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? String(parsed) : null;
}

function canonicalSalesSearch(search: string, tab: SalesTabKey): string {
  const sourcePairs = Array.from(new URLSearchParams(search).entries());
  const targetOwnedKeys = salesTabQueryKeySet(tab);
  const params = new URLSearchParams(search);
  const canonicalLead = tab === 'pipeline'
    ? positiveId(params.get('lead') ?? params.get('lead_id') ?? params.get('leadId'))
    : null;
  const firstLeadOrTabIndex = sourcePairs.findIndex(([key]) => (
    key === 'tab' || (tab === 'pipeline' && LEAD_QUERY_KEYS.has(key))
  ));
  const kept = sourcePairs.filter(([key]) => (
    key !== 'tab'
    && !(tab === 'pipeline' && LEAD_QUERY_KEYS.has(key))
    && (!ALL_SALES_QUERY_KEY_SET.has(key) || targetOwnedKeys.has(key))
    && (tab !== 'pipeline' || canonicalLead !== null || key !== 'leadTab')
  ));
  if (tab === 'pipeline' && canonicalLead) {
    const insertionIndex = firstLeadOrTabIndex < 0
      ? kept.length
      : sourcePairs
        .slice(0, firstLeadOrTabIndex)
        .filter(([key]) => (
          key !== 'tab'
          && !LEAD_QUERY_KEYS.has(key)
          && (!ALL_SALES_QUERY_KEY_SET.has(key) || targetOwnedKeys.has(key))
        ))
        .length;
    kept.splice(insertionIndex, 0, ['lead', canonicalLead]);
  }
  const serialized = new URLSearchParams(kept).toString();
  return serialized ? `?${serialized}` : '';
}

export function canonicalizeSalesLocation({
  location,
  accessibleTabs,
  defaultTab,
}: {
  location: SalesLocationLike;
  accessibleTabs: readonly string[];
  defaultTab: SalesTabKey;
}): CanonicalSalesLocation {
  const accessible = new Set(accessibleTabs.filter(isSalesTabKey));
  const params = new URLSearchParams(location.search);
  const legacyTab = params.get('tab');
  const tabFromPath = pathTab(location.pathname);
  const requestedTab = isSalesTabKey(legacyTab)
    ? legacyTab
    : isSalesTabKey(tabFromPath)
      ? tabFromPath
      : null;
  const tab = requestedTab && accessible.has(requestedTab)
    ? requestedTab
    : defaultTab;

  return {
    pathname: canonicalSalesTabPath(tab),
    search: canonicalSalesSearch(location.search, tab),
    hash: location.hash,
    tab,
  };
}

export function canonicalSalesTabTarget(
  tab: SalesTabKey,
  location: SalesLocationLike,
): SalesLocationLike {
  const sourcePairs = Array.from(new URLSearchParams(location.search).entries());
  const params = new URLSearchParams(location.search);
  const legacyTab = params.get('tab');
  const sourceTab = isSalesTabKey(legacyTab)
    ? legacyTab
    : isSalesTabKey(pathTab(location.pathname))
      ? pathTab(location.pathname) as SalesTabKey
      : null;
  const keptPairs = sourcePairs.filter(([key]) => (
    key !== 'tab'
    && (sourceTab === tab || !ALL_SALES_QUERY_KEY_SET.has(key))
  ));
  const search = new URLSearchParams(keptPairs).toString();
  const canonical = canonicalizeSalesLocation({
    location: {
      pathname: canonicalSalesTabPath(tab),
      search: search ? `?${search}` : '',
      hash: location.hash,
    },
    accessibleTabs: SALES_TAB_KEYS,
    defaultTab: tab,
  });
  return {
    pathname: canonical.pathname,
    search: canonical.search,
    hash: canonical.hash,
  };
}

/**
 * Builds the basename-free target consumed by React Router navigation surfaces.
 */
export function canonicalSalesLeadTarget(
  leadId: number,
  location: SalesLocationLike,
): SalesLocationLike | null {
  if (!Number.isSafeInteger(leadId) || leadId <= 0) return null;
  const onPipeline = pathTab(location.pathname) === 'pipeline';
  const params = new URLSearchParams(onPipeline ? location.search : '');
  params.delete('tab');
  params.delete('lead');
  params.delete('lead_id');
  params.delete('leadId');
  params.delete('leadTab');
  params.delete('panel');
  params.set('lead', String(leadId));
  const search = params.toString();
  return {
    pathname: canonicalSalesTabPath('pipeline'),
    search: search ? `?${search}` : '',
    hash: onPipeline ? location.hash : '',
  };
}

/**
 * Builds a document URL. The active shell basename is retained, while Pipeline
 * query/hash state is preserved only when the source document is Pipeline.
 */
export function canonicalSalesLeadUrl(
  leadId: number,
  location: SalesLocationLike,
): string | null {
  const basename = resolveAppBasename(location.pathname);
  const target = canonicalSalesLeadTarget(leadId, {
    pathname: toRouterPath(basename, location.pathname),
    search: location.search,
    hash: location.hash,
  });
  return target
    ? toDocumentPath(basename, `${target.pathname}${target.search}${target.hash}`)
    : null;
}

export function navigateToCanonicalSalesLead(leadId: number): void {
  if (typeof window === 'undefined') return;
  const target = canonicalSalesLeadUrl(leadId, window.location);
  if (target) window.location.assign(target);
}
